import { Fragment } from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, SectionHeading } from "@/components/motion";
import { COMPARE_PRODUCTS, COMPARE_SECTIONS, COMPARE_FOOTNOTES } from "@/data/frameComparison";

const tone = (v) => {
  const u = v.trim().toUpperCase();
  if (u.startsWith("YES")) return "yes";
  if (u === "NO" || u.startsWith("NOT SUGGESTED")) return "no";
  if (u === "?" || u === "N/A" || u === "—") return "na";
  return "text";
};

function Cell({ value, highlight }) {
  const t = tone(value);
  const u = value.trim().toUpperCase();
  const rest = t === "yes" || t === "no" ? value.replace(/^(YES|NOT SUGGESTED|NO|BAD|LOW)\b[\s—-]*/i, "") : value;
  return (
    <td className={`min-w-40 border-b border-girder px-4 py-3 align-top ${highlight ? "bg-hivis/[0.06]" : ""}`} data-testid="compare-cell">
      {t === "yes" && (
        <span className="flex items-start gap-2">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-hivis" />
          <span className="text-xs uppercase tracking-wider text-slate-200">{rest || "Yes"}</span>
        </span>
      )}
      {t === "no" && (
        <span className="flex items-start gap-2">
          <X className="mt-0.5 h-4 w-4 shrink-0 text-red-400/70" />
          <span className="text-xs uppercase tracking-wider text-slate-400">{u === "NO" ? "" : rest || value}</span>
        </span>
      )}
      {t === "na" && <span className="text-xs uppercase tracking-wider text-slate-600">{value}</span>}
      {t === "text" && <span className="text-xs leading-relaxed tracking-wide text-slate-300">{value}</span>}
    </td>
  );
}

export default function CompareFramesPage() {
  return (
    <div data-testid="compare-frames-page">
      <PageHeader
        kicker="Frame Comparison"
        title="The Banner Bar vs. common alternatives"
        sub="A straight, side-by-side look at five freestanding banner frame approaches — The Banner Bar, Fiber Frame, Banner A-Frame, galvanized steel banner frame and everyday T-posts & rope — so you can get the best frame for your job."
        image="/assets/img/w-gallery-img-5061.jpg"
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20" data-testid="compare-frames-section">
        <Reveal i={1}>
          <p className="max-w-3xl text-sm leading-relaxed text-slate-400 md:text-base">
            Shopping freestanding, in-ground metal banner frames? Every approach on this chart will hold a banner up on a calm day.
            The differences show up in the wind, in week six, and at banner-change time. Photos show real installs of each system.
          </p>
        </Reveal>

        <Reveal i={2}>
          <div className="mt-10 overflow-x-auto border border-girder" data-testid="frame-comparison-table">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 min-w-44 border-b border-r border-girder bg-obsidian px-4 py-3 text-left align-bottom">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Feature</span>
                  </th>
                  {COMPARE_PRODUCTS.map((p) => (
                    <th key={p.key} className={`min-w-44 border-b border-girder px-4 py-3 text-left align-bottom ${p.ours ? "bg-hivis/[0.08] outline outline-1 -outline-offset-1 outline-hivis/50" : "bg-steel/40"}`} data-testid={`compare-col-${p.key}`}>
                      <div className="clip-corner-sm mb-3 overflow-hidden border border-girder">
                        <img src={p.photo} alt={`${p.name} install`} loading="lazy" className="h-24 w-full object-cover" />
                      </div>
                      <span className={`font-display text-base font-bold uppercase leading-tight ${p.ours ? "text-hivis" : "text-slate-200"}`}>{p.name}</span>
                      {p.ours && <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-hivis/70">Patented · Made in USA</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_SECTIONS.map((sec) => (
                  <Fragment key={sec.title}>
                    <tr>
                      <th colSpan={6} className="sticky left-0 border-b border-girder bg-steel/70 px-4 py-2.5 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-hivis">
                        {sec.title}
                      </th>
                    </tr>
                    {sec.rows.map((row) => (
                      <tr key={row.label} className="transition-colors hover:bg-steel/20">
                        <th className="sticky left-0 z-10 border-b border-r border-girder bg-obsidian px-4 py-3 text-left align-top font-mono text-[11px] font-normal uppercase tracking-wider text-slate-400">
                          {row.label}
                        </th>
                        {row.values.map((v, i) => (
                          <Cell key={i} value={v} highlight={i === 0} />
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal i={3}>
          <div className="mt-4 space-y-1">
            {COMPARE_FOOTNOTES.map((f) => (
              <p key={f} className="font-mono text-[10px] uppercase tracking-widest text-slate-600">{f}</p>
            ))}
          </div>
        </Reveal>

        <Reveal i={4}>
          <div className="clip-corner mt-14 border border-girder bg-obsidian p-8 text-center" data-testid="compare-frames-cta">
            <h3 className="font-display text-2xl font-bold uppercase text-slate-100 md:text-3xl">Ready for the professional option?</h3>
            <p className="mx-auto mt-2 max-w-xl text-sm text-slate-400">Four kits, seven banner configurations, replacement parts always in stock — and a guarantee it can't collapse.</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              <Link to="/products" data-testid="compare-frames-shop-cta" className="clip-corner-sm inline-flex items-center gap-2 bg-hivis px-6 py-3 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">
                Shop the kits <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/dealer-locator" data-testid="compare-frames-dealer-cta" className="clip-corner-sm inline-flex items-center gap-2 border border-girder px-6 py-3 font-display font-bold uppercase text-slate-200 transition-colors hover:border-hivis hover:text-hivis">
                Find a dealer
              </Link>
              <Link to="/compare" data-testid="compare-frames-kits-cta" className="font-mono text-xs uppercase tracking-widest text-slate-400 underline decoration-girder underline-offset-4 transition-colors hover:text-hivis">
                Compare our four kits
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
